/**
 * Authentication API
 */

import apiClient, { TokenStorage } from "./client";
import type {
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  PasswordResetRequest,
  PasswordResetConfirm,
  UserProfile,
  UpdateProfileRequest,
  TokenPair,
  RefreshTokenRequest,
} from "@/types/auth";

export const authApi = {
  /**
   * User login. `withCredentials` is required so the browser stores the
   * HttpOnly refresh cookie set by the login response (cross-origin, same-site).
   */
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>("/api/auth/v1/login", data, {
      withCredentials: true,
    }),

  /**
   * User logout. `withCredentials` is required so the request carries the
   * HttpOnly refresh cookie and the browser honors the backend's
   * cookie-clearing Set-Cookie response.
   */
  logout: (options: { skipToast?: boolean } = {}) =>
    apiClient.post(
      "/api/auth/v1/logout",
      { token: TokenStorage.getAccessToken() },
      { withCredentials: true, _skipToast: options.skipToast } as Record<string, unknown>,
    ),

  /**
   * Refresh access token
   */
  refreshToken: (data: RefreshTokenRequest) =>
    apiClient.post<TokenPair>("/api/auth/v1/refresh", data),

  /**
   * Request password reset
   */
  requestPasswordReset: (data: PasswordResetRequest) =>
    apiClient.post("/api/auth/v1/password-reset-request", data),

  /**
   * Confirm password reset with token
   */
  confirmPasswordReset: (data: PasswordResetConfirm) =>
    apiClient.post("/api/auth/v1/password-reset-confirm", data),

  /**
   * Change password (authenticated)
   */
  changePassword: async (data: ChangePasswordRequest) => {
    // A password change revokes every login made before it, this browser's
    // included, so the server answers with a new login for this browser: an
    // access token here and a new refresh cookie (hence withCredentials, which
    // also sends the old cookie so the "remember me" choice carries over).
    // The other tabs share that cookie and pick the new login up on their next
    // refresh. Unticked logins say so, as a refresh does (it can only shorten).
    const response = await apiClient.post<ChangePasswordResponse>(
      "/api/auth/v1/change-password",
      TokenStorage.isRememberMe() ? data : { ...data, remember_me: false },
      // Each form shows its own error (a wrong current password is a 400,
      // which the client would otherwise toast a second time).
      { withCredentials: true, _skipToast: true } as Record<string, unknown>,
    );
    const tokens = response.data?.tokens;
    if (tokens?.access_token) {
      TokenStorage.setTokens(tokens.access_token, "", tokens.expires_in);
    }
    return response;
  },

  /**
   * Get current user profile
   */
  getProfile: (options: { skipToast?: boolean } = {}) =>
    apiClient.get<UserProfile>("/api/auth/v1/me", {
      _skipToast: options.skipToast,
    } as Record<string, unknown>),

  /**
   * Update current user profile
   */
  updateProfile: (data: UpdateProfileRequest) =>
    apiClient.patch<UserProfile>("/api/auth/v1/me", data),
};
